<script>
/**
 * @component FamilyGroupManager
 * @description UC-REG-295 — Gestión permanente del grupo familiar primario
 *              por el propio vecino (Campana): lista de integrantes a cargo
 *              con alta, edición y baja contra `/family-group/me`. El titular
 *              gestiona; un integrante con cuenta propia solo ve el grupo.
 *              Cada integrante despliega su credencial municipal al tocarlo.
 * @example <FamilyGroupManager />
 */
import familyGroupService from "@/services/family-group";
import MunicipalCredentialCard from "@/components/patient/MunicipalCredentialCard.vue";
import {
  FAMILY_MEMBER_TYPE_OPTIONS,
  familyGroupErrorMessage,
  familyMemberTypeLabel,
  formatDni,
  titleCase,
  toMunicipalCredential,
} from "@/services/credential";

/** @returns {Object} Form vacío del integrante */
const emptyForm = () => ({
  firstName: "",
  lastName: "",
  dni: "",
  bornAt: "",
  gender: "",
  type: "child",
});

export default {
  name: "FamilyGroupManager",

  components: { MunicipalCredentialCard },

  data() {
    return {
      /** @type {Object|null} titular del grupo (según la API) */
      holder: null,
      /** @type {boolean} si el logueado es el titular (puede gestionar) */
      isHolder: true,
      /** @type {Array<Object>} integrantes (sin el titular) */
      members: [],
      isLoading: true,
      /** @type {boolean} true si la API no tiene el grupo familiar habilitado (404) */
      isUnavailable: false,
      dialogVisible: false,
      isSaving: false,
      /** @type {Object|null} integrante en edición (null = alta) */
      editing: null,
      form: emptyForm(),
      /** @type {Object<string,string>} errores inline del form */
      formErrors: {},
      /** @type {string|null} _id del integrante cuya credencial está desplegada */
      expandedId: null,
      typeOptions: FAMILY_MEMBER_TYPE_OPTIONS,
      genderOptions: [
        { value: "f", label: "Femenino" },
        { value: "m", label: "Masculino" },
        { value: "x", label: "X / no binario" },
      ],
    };
  },

  computed: {
    /** @returns {string} */
    dialogTitle() {
      return this.editing ? "Editar integrante" : "Agregar integrante";
    },

    /** @returns {boolean} En edición de alguien con cuenta propia solo se cambia el parentesco */
    onlyTypeEditable() {
      return Boolean(this.editing && this.editing.hasOwnAccount);
    },
  },

  created() {
    this.load();
  },

  methods: {
    formatDni,
    familyMemberTypeLabel,

    /**
     * @description Carga el grupo. 404 = la API del tenant no tiene la
     *              autogestión habilitada → se oculta la sección sin error.
     */
    async load() {
      this.isLoading = true;
      try {
        const data = await familyGroupService.getMe({
          getErrorMessage: (err) =>
            err && err.response && err.response.status === 404
              ? null
              : familyGroupErrorMessage(err),
        });
        this.applyGroup(data);
      } catch (err) {
        if (err && err.response && err.response.status === 404) {
          this.isUnavailable = true;
        }
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * @description Aplica una respuesta de la API al estado.
     * @param {{holder: Object, isHolder: boolean, members: Array<Object>}} data
     */
    applyGroup(data) {
      this.holder = (data && data.holder) || null;
      this.isHolder = !data || data.isHolder !== false;
      this.members = (data && data.members) || [];
      this.$emit("updated", {
        holder: this.holder,
        isHolder: this.isHolder,
        members: this.members,
      });
    },

    /**
     * @description Nombre Apellido de un integrante.
     * @param {Object} m
     * @returns {string}
     */
    fullName(m) {
      return `${titleCase(m.firstName)} ${titleCase(m.lastName)}`.trim();
    },

    /**
     * @description Iniciales para el avatar.
     * @param {Object} m
     * @returns {string}
     */
    initials(m) {
      const first = (m.firstName || "")[0] || "";
      const last = (m.lastName || "")[0] || "";
      return `${first}${last}`.toUpperCase();
    },

    /**
     * @description Fecha de nacimiento dd/mm/aaaa.
     * @param {Object} m
     * @returns {string}
     */
    birthLabel(m) {
      if (!m.bornAt) return "";
      const d = new Date(m.bornAt);
      if (Number.isNaN(d.getTime())) return "";
      return d.toLocaleDateString("es-AR", { timeZone: "UTC" });
    },

    /**
     * @description Credencial municipal de un integrante (para desplegar).
     * @param {Object} m
     * @returns {Object|null}
     */
    credentialOf(m) {
      return toMunicipalCredential(m);
    },

    /** @param {Object} m */
    toggleExpanded(m) {
      this.expandedId = this.expandedId === m._id ? null : m._id;
    },

    /** @description Abre el form de alta. */
    openCreate() {
      this.editing = null;
      this.form = emptyForm();
      this.formErrors = {};
      this.dialogVisible = true;
    },

    /**
     * @description Abre el form de edición con los datos del integrante.
     * @param {Object} m
     */
    openEdit(m) {
      this.editing = m;
      this.form = {
        firstName: titleCase(m.firstName),
        lastName: titleCase(m.lastName),
        dni: m.dni || "",
        bornAt: m.bornAt ? String(m.bornAt).slice(0, 10) : "",
        gender: m.gender || "",
        type: m.type || "child",
      };
      this.formErrors = {};
      this.dialogVisible = true;
    },

    /**
     * @description Validación inline del form (espejo de la de la API).
     * @returns {boolean} true si es válido
     */
    validate() {
      const errors = {};
      if (!this.onlyTypeEditable) {
        if (!this.form.firstName.trim())
          errors.firstName = "Ingresá el nombre.";
        if (!this.form.lastName.trim())
          errors.lastName = "Ingresá el apellido.";
        if (!this.editing) {
          const dni = String(this.form.dni || "").replace(/[^0-9]/g, "");
          if (!/^[0-9]{7,8}$/.test(dni))
            errors.dni = "El DNI tiene que tener 7 u 8 números.";
        }
        const born = this.form.bornAt ? new Date(this.form.bornAt) : null;
        if (!born || Number.isNaN(born.getTime()) || born > new Date()) {
          errors.bornAt = "Ingresá una fecha de nacimiento válida.";
        }
      }
      if (!this.form.type) errors.type = "Elegí el parentesco.";
      this.formErrors = errors;
      return Object.keys(errors).length === 0;
    },

    /**
     * @description Payload que viaja a la API.
     * @returns {Object}
     */
    buildPayload() {
      if (this.onlyTypeEditable) {
        return { type: this.form.type };
      }
      const payload = {
        firstName: this.form.firstName.trim(),
        lastName: this.form.lastName.trim(),
        bornAt: this.form.bornAt,
        type: this.form.type,
      };
      if (this.form.gender) payload.gender = this.form.gender;
      if (!this.editing) {
        payload.dni = String(this.form.dni || "").replace(/[^0-9]/g, "");
      }
      return payload;
    },

    /** @description Guarda (alta o edición). */
    async save() {
      if (!this.validate()) return;
      this.isSaving = true;
      try {
        const payload = this.buildPayload();
        const data = this.editing
          ? await familyGroupService.updateMember(this.editing._id, payload)
          : await familyGroupService.addMember(payload);
        this.applyGroup(data);
        this.dialogVisible = false;
        this.$message.success(
          this.editing
            ? "Integrante actualizado."
            : "Integrante agregado a tu grupo familiar."
        );
      } catch (err) {
        // El mensaje ya lo mostró la capa HTTP (códigos traducidos en
        // services/credential.js); acá solo dejamos el form abierto.
      } finally {
        this.isSaving = false;
      }
    },

    /**
     * @description Confirma y quita a un integrante del grupo.
     * @param {Object} m
     */
    confirmRemove(m) {
      this.$confirm(
        `¿Querés quitar a ${this.fullName(
          m
        )} de tu grupo familiar? Sus datos de salud no se borran.`,
        "Quitar integrante",
        {
          confirmButtonText: "Quitar",
          cancelButtonText: "Cancelar",
          type: "warning",
        }
      )
        .then(() => this.remove(m))
        .catch(() => {});
    },

    /**
     * @description Quita al integrante.
     * @param {Object} m
     */
    async remove(m) {
      this.isLoading = true;
      try {
        const data = await familyGroupService.removeMember(m._id);
        this.applyGroup(data);
        if (this.expandedId === m._id) this.expandedId = null;
        this.$message.success("Integrante quitado del grupo familiar.");
      } catch (err) {
        // mensaje mostrado por la capa HTTP
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>

<template lang="pug">
.fg(v-if="!isUnavailable")
  .fg__header
    .fg__header-left
      h2.fg__title Grupo familiar
      span.fg__badge {{ members.length }}
    el-button.fg__add(
      v-if="isHolder"
      type="primary"
      size="small"
      icon="el-icon-plus"
      @click="openCreate"
    ) Agregar integrante

  p.fg__hint(v-if="isHolder") Cargá a las personas a tu cargo (hijos/as, pareja, familiares). Vas a poder ver y actualizar sus datos cuando quieras.
  p.fg__hint(v-else) Formás parte del grupo familiar de {{ holder ? fullName(holder) : 'tu titular' }}. Los cambios los hace el titular.

  div(v-loading="isLoading")
    .fg__member.fg__member--holder(v-if="!isHolder && holder")
      .fg__avatar {{ initials(holder) }}
      .fg__info
        span.fg__name {{ fullName(holder) }}
        span.fg__sub DNI {{ formatDni(holder.dni) }}
      span.fg__tag.fg__tag--holder Titular

    p.fg__empty(v-if="!isLoading && !members.length && isHolder") Todavía no cargaste integrantes.

    .fg__item(v-for="m in members" :key="m._id")
      .fg__member(@click="toggleExpanded(m)" :class="{ 'is-expanded': expandedId === m._id }")
        .fg__avatar {{ initials(m) }}
        .fg__info
          span.fg__name {{ fullName(m) }}
          span.fg__sub DNI {{ formatDni(m.dni) }}
            template(v-if="birthLabel(m)")  · Nac. {{ birthLabel(m) }}
        span.fg__tag {{ familyMemberTypeLabel(m.type) }}
        .fg__actions(v-if="isHolder")
          button.fg__icon-btn(type="button" title="Editar" @click.stop="openEdit(m)")
            micon(name="edit")
          button.fg__icon-btn.fg__icon-btn--danger(type="button" title="Quitar del grupo" @click.stop="confirmRemove(m)")
            micon(name="delete_forever")
      municipal-credential-card.fg__credential(
        v-if="expandedId === m._id"
        :person="credentialOf(m)"
        :tag="familyMemberTypeLabel(m.type)"
      )

  el-dialog(
    :visible.sync="dialogVisible"
    :title="dialogTitle"
    width="440px"
    append-to-body
    :close-on-click-modal="false"
  )
    .fg__form
      p.fg__form-note(v-if="onlyTypeEditable") {{ fullName(editing) }} tiene su propia cuenta: sus datos personales los administra desde su usuario. Acá solo podés cambiar el parentesco.
      template(v-if="!onlyTypeEditable")
        .fg__field
          label.fg__label(for="fg-firstName") Nombre
          el-input#fg-firstName(v-model="form.firstName" placeholder="Nombre")
          span.fg__error(v-if="formErrors.firstName") {{ formErrors.firstName }}
        .fg__field
          label.fg__label(for="fg-lastName") Apellido
          el-input#fg-lastName(v-model="form.lastName" placeholder="Apellido")
          span.fg__error(v-if="formErrors.lastName") {{ formErrors.lastName }}
        .fg__field(v-if="!editing")
          label.fg__label(for="fg-dni") DNI
          el-input#fg-dni(v-model="form.dni" placeholder="Solo números" inputmode="numeric")
          span.fg__error(v-if="formErrors.dni") {{ formErrors.dni }}
        .fg__field
          label.fg__label(for="fg-bornAt") Fecha de nacimiento
          el-date-picker#fg-bornAt(
            v-model="form.bornAt"
            type="date"
            value-format="yyyy-MM-dd"
            format="dd/MM/yyyy"
            placeholder="dd/mm/aaaa"
            style="width: 100%"
          )
          span.fg__error(v-if="formErrors.bornAt") {{ formErrors.bornAt }}
        .fg__field
          label.fg__label(for="fg-gender") Sexo
          el-select#fg-gender(v-model="form.gender" placeholder="Opcional" clearable style="width: 100%")
            el-option(v-for="g in genderOptions" :key="g.value" :value="g.value" :label="g.label")
      .fg__field
        label.fg__label(for="fg-type") Parentesco
        el-select#fg-type(v-model="form.type" placeholder="Elegí el parentesco" style="width: 100%")
          el-option(v-for="t in typeOptions" :key="t.value" :value="t.value" :label="t.label")
        span.fg__error(v-if="formErrors.type") {{ formErrors.type }}
    div(slot="footer")
      el-button(@click="dialogVisible = false") Cancelar
      el-button(type="primary" :loading="isSaving" @click="save") Guardar
</template>

<style lang="scss" scoped>
.fg {
  margin-bottom: 24px;

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    margin-bottom: 6px;
  }

  &__header-left {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  &__title {
    font-size: 14px;
    font-weight: 700;
    color: $navy;
    margin: 0;
  }

  &__badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 20px;
    height: 20px;
    padding: 0 6px;
    border-radius: 10px;
    background: #f3f4f6;
    color: #6b7280;
    font-size: 11px;
    font-weight: 700;
  }

  &__hint {
    font-size: 12px;
    color: #6b7280;
    margin: 0 0 12px;
  }

  &__empty {
    font-size: 13px;
    color: #6b7280;
    padding: 16px;
    border: 1px dashed #d1d5db;
    border-radius: 10px;
    text-align: center;
    margin: 0 0 8px;
  }

  &__item {
    margin-bottom: 8px;
  }

  &__member {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 14px;
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 10px;
    cursor: pointer;
    transition: border-color 0.15s ease;

    &:hover,
    &.is-expanded {
      border-color: var(--org-color-primary, $primary);
    }

    &--holder {
      cursor: default;
      margin-bottom: 8px;
    }
  }

  &__avatar {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: #f3f4f6;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 13px;
    font-weight: 700;
    color: $navy;
    flex-shrink: 0;
  }

  &__info {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-width: 0;
  }

  &__name {
    font-size: 13px;
    font-weight: 700;
    color: $navy;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__sub {
    font-size: 11px;
    color: #6b7280;
  }

  &__tag {
    display: inline-flex;
    align-items: center;
    padding: 3px 10px;
    border-radius: 6px;
    font-size: 11px;
    font-weight: 600;
    background: #f3f4f6;
    color: #6b7280;
    flex-shrink: 0;

    &--holder {
      background: var(--org-color-primary, $primary);
      color: #fff;
    }
  }

  &__actions {
    display: flex;
    gap: 4px;
    flex-shrink: 0;
  }

  &__icon-btn {
    width: 32px;
    height: 32px;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    background: #fff;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;

    .micon {
      width: 16px;
      height: 16px;
      fill: $navy;
    }

    &:hover {
      border-color: var(--org-color-primary, $primary);
    }

    &--danger:hover {
      border-color: #e9595b;
      .micon {
        fill: #e9595b;
      }
    }
  }

  &__credential {
    margin-top: 8px;
    margin-bottom: 0;
  }

  &__form {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  &__form-note {
    font-size: 12px;
    color: #6b7280;
    margin: 0;
  }

  &__field {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  &__label {
    font-size: 12px;
    font-weight: 600;
    color: $navy;
  }

  &__error {
    font-size: 11px;
    color: #e9595b;
  }
}

@media (max-width: 767px) {
  .fg {
    &__header {
      flex-wrap: wrap;
    }

    &__member {
      padding: 10px 12px;
      flex-wrap: wrap;
    }

    &__tag {
      order: 3;
    }
  }
}
</style>
